import { useState } from "react";
import api from "../api";
import { useConfirm } from "./ConfirmDialog";
import { AttachmentList, AttachmentComposer } from "./Attachments";

const CAT_LABEL = { upgrade: "Upgrade", venture: "New Venture" };
const CAT_ACCENT = { upgrade: "a-green", venture: "a-blue" };

export default function ThreadsPanel({ state, setState, isAdmin }) {
  const confirm = useConfirm();
  const [inp, setInp] = useState("");
  const [newCat, setNewCat] = useState("venture");
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({});
  const [catFilter, setCatFilter] = useState("all"); // all | upgrade | venture
  const [notesOpen, setNotesOpen] = useState({}); // { [threadIdx]: bool }
  const [noteDraft, setNoteDraft] = useState({}); // { [threadIdx]: string }
  const [notePending, setNotePending] = useState({}); // { [threadIdx]: File[] }

  const replace = (threads) => setState({ ...state, threads });

  const add = async () => {
    if (!inp.trim()) return;
    const body = {
      title: inp.trim(),
      text: "",
      category: newCat,
      level: CAT_ACCENT[newCat],
    };
    const { data } = await api.post("/threads", body);
    replace([...state.threads, data]);
    setInp("");
    setNewCat("venture");
  };

  const del = async (i) => {
    const t = state.threads[i];
    const ok = await confirm({
      title: "Delete this opportunity thread?",
      message: `"${t?.title || ""}"\n\nThis cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await api.delete(`/threads/${i}`);
    replace(state.threads.filter((_, j) => j !== i));
  };

  const startEdit = (i) => {
    setEditing(i);
    setEdit({ ...state.threads[i] });
  };

  const save = async () => {
    const i = editing;
    const { data } = await api.patch(`/threads/${i}`, edit);
    replace(state.threads.map((x, j) => (j === i ? { ...x, ...data } : x)));
    setEditing(null);
  };

  const toggleCategory = async (i) => {
    const cur = state.threads[i];
    const nextCat = cur.category === "upgrade" ? "venture" : "upgrade";
    const { data } = await api.patch(`/threads/${i}`, {
      category: nextCat,
      level: CAT_ACCENT[nextCat],
    });
    replace(state.threads.map((x, j) => (j === i ? { ...x, ...data } : x)));
  };

  const addNote = async (i) => {
    const body = (noteDraft[i] || "").trim();
    const files = notePending[i] || [];
    if (!body && files.length === 0) return;
    const { data } = await api.post(`/threads/${i}/notes`, {
      body: body || "(attachment)",
    });
    let newNote = data;
    if (files.length > 0) {
      const newNi = (state.threads[i].notes || []).length; // about-to-be index
      const uploaded = [];
      for (const f of files) {
        const form = new FormData();
        form.append("file", f);
        try {
          const res = await api.post(
            `/threads/${i}/notes/${newNi}/attachments`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          uploaded.push(res.data);
        } catch (err) {
          alert(`Attachment "${f.name}" failed: ` + (err.response?.data?.detail || err.message));
        }
      }
      newNote = { ...newNote, attachments: uploaded };
    }
    replace(
      state.threads.map((x, j) =>
        j === i ? { ...x, notes: [...(x.notes || []), newNote] } : x
      )
    );
    setNoteDraft({ ...noteDraft, [i]: "" });
    setNotePending({ ...notePending, [i]: [] });
  };

  const delNoteAttachment = async (i, ni, ai) => {
    await api.delete(`/threads/${i}/notes/${ni}/attachments/${ai}`);
    replace(
      state.threads.map((x, j) =>
        j === i
          ? {
              ...x,
              notes: (x.notes || []).map((n, k) =>
                k === ni
                  ? { ...n, attachments: (n.attachments || []).filter((_, a) => a !== ai) }
                  : n
              ),
            }
          : x
      )
    );
  };

  const delNote = async (i, ni) => {
    const n = (state.threads[i].notes || [])[ni];
    const ok = await confirm({
      title: "Delete this note?",
      message: `"${(n?.body || "").slice(0, 120)}${(n?.body || "").length > 120 ? "…" : ""}"\n\nThis cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await api.delete(`/threads/${i}/notes/${ni}`);
    replace(
      state.threads.map((x, j) =>
        j === i ? { ...x, notes: (x.notes || []).filter((_, k) => k !== ni) } : x
      )
    );
  };

  const upgradeCount = state.threads.filter((t) => t.category === "upgrade").length;
  const ventureCount = state.threads.filter((t) => t.category === "venture").length;

  const visible = state.threads
    .map((t, i) => ({ t, i }))
    .filter((r) => catFilter === "all" || r.t.category === catFilter);

  return (
    <div data-testid="panel-threads">
      <div className="sec-head">Opportunity Threads</div>
      <div className="sec-desc">
        Upgrades to the current business vs. new venture bets. Click the category chip on any
        thread to flip it.
      </div>

      {isAdmin && (
        <div className="add-row">
          <input
            className="grow"
            placeholder="New opportunity thread..."
            value={inp}
            onChange={(e) => setInp(e.target.value)}
            data-testid="thread-input"
          />
          <div className="seg" style={{ marginTop: 0 }}>
            <button
              type="button"
              className={newCat === "upgrade" ? "on" : ""}
              onClick={() => setNewCat("upgrade")}
              data-testid="thread-new-upgrade"
            >
              Upgrade
            </button>
            <button
              type="button"
              className={newCat === "venture" ? "on" : ""}
              onClick={() => setNewCat("venture")}
              data-testid="thread-new-venture"
            >
              New Venture
            </button>
          </div>
          <button className="btn" onClick={add} data-testid="thread-add">
            Add
          </button>
        </div>
      )}

      <div className="filter-row">
        <span className="flabel">Show</span>
        <button
          className={`chip-toggle cat-chip cat-upgrade ${catFilter === "upgrade" ? "on" : ""}`}
          onClick={() => setCatFilter(catFilter === "upgrade" ? "all" : "upgrade")}
          data-testid="filter-upgrade"
        >
          Upgrades · {upgradeCount}
        </button>
        <button
          className={`chip-toggle cat-chip cat-venture ${catFilter === "venture" ? "on" : ""}`}
          onClick={() => setCatFilter(catFilter === "venture" ? "all" : "venture")}
          data-testid="filter-venture"
        >
          New Ventures · {ventureCount}
        </button>
        {catFilter !== "all" && (
          <button
            className="chip-toggle"
            onClick={() => setCatFilter("all")}
            data-testid="filter-clear"
          >
            Show all
          </button>
        )}
      </div>

      {visible.length === 0 ? (
        <div className="empty-note">No threads match the current filter.</div>
      ) : (
        visible.map(({ t, i }) => {
          const cat = t.category || "venture";
          const accent = CAT_ACCENT[cat];
          return (
            <div className="item" key={t.id || i} data-testid={`thread-${i}`}>
              <div className={`item-accent ${accent}`} />
              <div className="item-main">
                {editing === i ? (
                  <>
                    <input
                      className="inline-edit"
                      value={edit.title || ""}
                      onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                    />
                    <textarea
                      className="inline-edit"
                      value={edit.text || ""}
                      onChange={(e) => setEdit({ ...edit, text: e.target.value })}
                    />
                    <div className="seg" style={{ marginTop: 0, marginBottom: 8 }}>
                      <button
                        type="button"
                        className={(edit.category || "venture") === "upgrade" ? "on" : ""}
                        onClick={() =>
                          setEdit({ ...edit, category: "upgrade", level: CAT_ACCENT.upgrade })
                        }
                      >
                        Upgrade
                      </button>
                      <button
                        type="button"
                        className={(edit.category || "venture") === "venture" ? "on" : ""}
                        onClick={() =>
                          setEdit({ ...edit, category: "venture", level: CAT_ACCENT.venture })
                        }
                      >
                        New Venture
                      </button>
                    </div>
                    <button className="btn btn-sm" onClick={save}>
                      Save
                    </button>
                  </>
                ) : (
                  <>
                    <div className="item-title" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {t.title}
                      <button
                        type="button"
                        className={`cat-chip-row cat-${cat} ${isAdmin ? "clickable" : ""}`}
                        onClick={isAdmin ? () => toggleCategory(i) : undefined}
                        title={isAdmin ? "Click to flip category" : undefined}
                        data-testid={`thread-${i}-category`}
                      >
                        {CAT_LABEL[cat]}
                      </button>
                    </div>
                    <div className="item-text">{t.text}</div>

                    {/* NOTES */}
                    <div className="thread-notes-wrap">
                      <button
                        type="button"
                        className="thread-notes-toggle"
                        onClick={() => setNotesOpen({ ...notesOpen, [i]: !notesOpen[i] })}
                        data-testid={`thread-${i}-notes-toggle`}
                      >
                        {notesOpen[i] ? "▾" : "▸"} Notes
                        <span className="archive-count">
                          {(t.notes || []).length}
                        </span>
                      </button>
                      {notesOpen[i] && (
                        <div className="thread-notes" data-testid={`thread-${i}-notes`}>
                          {(t.notes || []).length === 0 ? (
                            <div className="deliv-empty" style={{ marginBottom: 8 }}>
                              No notes yet.
                            </div>
                          ) : (
                            (t.notes || []).map((n, ni) => (
                              <div
                                className={`cmt st-acknowledged`}
                                key={n.id || ni}
                                data-testid={`thread-${i}-note-${ni}`}
                              >
                                <div className="cmt-top">
                                  <span className={`cmt-author by-${n.author}`}>
                                    {n.author === "WH" ? "Ward" : "Marco"}
                                  </span>
                                  <span className="cmt-when">{n.at}</span>
                                </div>
                                <div className="cmt-body">{n.body}</div>
                                <AttachmentList
                                  items={n.attachments || []}
                                  canDelete={isAdmin}
                                  onDelete={(ai) => delNoteAttachment(i, ni, ai)}
                                  testIdPrefix={`thread-${i}-note-${ni}-att`}
                                />
                                {isAdmin && (
                                  <div className="cmt-actions">
                                    <button
                                      onClick={() => delNote(i, ni)}
                                      data-testid={`thread-${i}-note-${ni}-delete`}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))
                          )}
                          <div className="cmt-add">
                            <textarea
                              placeholder="Add a note about this opportunity..."
                              value={noteDraft[i] || ""}
                              onChange={(e) =>
                                setNoteDraft({ ...noteDraft, [i]: e.target.value })
                              }
                              data-testid={`thread-${i}-note-input`}
                            />
                            <AttachmentComposer
                              pending={notePending[i] || []}
                              setPending={(arr) =>
                                setNotePending({ ...notePending, [i]: arr })
                              }
                              testIdPrefix={`thread-${i}-note-att`}
                            />
                            <button
                              className="btn btn-sm"
                              onClick={() => addNote(i)}
                              data-testid={`thread-${i}-note-post`}
                            >
                              Post as {isAdmin ? "Marco" : "Ward"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
              {isAdmin && (
                <>
                  <button
                    className="edit-pencil"
                    onClick={() => startEdit(i)}
                    data-testid={`thread-${i}-edit`}
                  >
                    ✎
                  </button>
                  <button
                    className="del-x"
                    onClick={() => del(i)}
                    data-testid={`thread-${i}-delete`}
                  >
                    ×
                  </button>
                </>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
